/**
 * Bindings (architecture §8, D-4): the thin layer between command-agnostic
 * widgets and the Command Bus. A widget edits a value; a binding decides what
 * that value is and which command changes it.
 *
 * - `useComponentPropBinding` → `entity.setProperty`, live, with one edit
 *   session per focused edit, so typing a title is one undo step (AC 4).
 *   Over several entities, each change is one transaction of a command per
 *   entity, and the session's transactions coalesce into one undo step.
 * - `useSettingBinding` → `editor.setSetting` (Preferences).
 * - `useThemeBinding` → `theme.set` (Preferences).
 *
 * The Inspector and Preferences share every widget through these.
 */
import { useEffect, useRef, useState } from "react";
import type { InteractionSession } from "../../commands/CommandBus";
import type { CommandOrigin, CommandRequest } from "../../commands/types";
import type { EntityId, EntityRecord } from "../../world/entity";
import { useEditor } from "../EditorContext";
import { useEditorSettings, useEntities, useThemeChoice } from "../hooks/editorHooks";
import type { SettingKey } from "../settings/EditorSettings";
import { resolveEdit, sharedValue } from "./mixedValues";

/** What a widget needs: the current value, a live change, and the end of an edit. */
export interface FieldBinding {
  readonly value: unknown;
  /** Applies a new value now (coalesced into the current edit session). */
  readonly onChange: (value: unknown) => void;
  /** Ends the edit (blur, Enter, or a discrete control like a checkbox). */
  readonly onCommit: () => void;
  /** Why the last change was rejected, or null. */
  readonly error: string | null;
}

/**
 * One component prop over one or more entities: the value they share (or
 * `MIXED`), and edits that set it on all of them.
 */
export function useComponentPropBinding(
  ids: readonly EntityId[],
  component: string,
  key: string,
): FieldBinding {
  const entities = useEntities(ids);
  const prop = useEditor().components.get(component)?.props[key];
  const session = useEditSession(`${component}.${key}`);
  const send = useSend("inspector");
  const own = (entity: EntityRecord) => entity.components.get(component)?.[key];
  const values = entities.map(own);

  return {
    value: prop ? sharedValue(prop, values) : values[0],
    onChange: (value) => {
      const id = session.id();
      const requests = entities.map((entity) => ({
        type: "entity.setProperty",
        payload: {
          entity: entity.id,
          component,
          prop: key,
          value: resolveEdit(value, own(entity)),
          session: id,
        },
      }));
      send.runAll(`Set ${component}.${key} on ${entities.length} entities`, requests);
    },
    onCommit: session.end,
    error: send.error,
  };
}

export function useSettingBinding(key: SettingKey): FieldBinding {
  const settings = useEditorSettings();
  const send = useSend("preferences");

  return {
    value: settings[key],
    onChange: (value) => {
      send.run({ type: "editor.setSetting", payload: { key, value } });
    },
    // Settings aren't undoable (A-15), so there is no session to end.
    onCommit: () => undefined,
    error: send.error,
  };
}

export function useThemeBinding(): FieldBinding {
  const choice = useThemeChoice();
  const send = useSend("preferences");

  return {
    value: choice,
    onChange: (value) => {
      send.run({ type: "theme.set", payload: { id: value } });
    },
    // Not undoable (A-15): nothing to end.
    onCommit: () => undefined,
    error: send.error,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────

type Request = Omit<CommandRequest, "origin">;

/** Dispatches with an origin and keeps the last validation error for the widget. */
function useSend(origin: CommandOrigin) {
  const { dispatch, dispatchAll } = useEditor();
  const [error, setError] = useState<string | null>(null);

  const run = (request: Request) => {
    const result = dispatch({ ...request, origin });
    // Inspector and settings commands are synchronous.
    if (!(result instanceof Promise)) setError(result.ok ? null : result.error);
  };

  return {
    error,
    run,
    /** Several requests as one undo step (a lone one stays a plain command). */
    runAll: (label: string, requests: readonly Request[]) => {
      const [only] = requests;
      if (requests.length === 1 && only) {
        run(only);
        return;
      }
      const results = dispatchAll(
        label,
        requests.map((request) => ({ ...request, origin })),
      );
      const failed = results.find((result) => !result.ok);
      setError(failed?.ok === false ? failed.error : null);
    },
  };
}

let sessionCount = 0;

/**
 * One edit session: every change until `end()` (or an undo/redo, which aborts
 * the bus session) carries the same id and merges into one undo entry.
 */
function useEditSession(name: string) {
  const { bus } = useEditor();
  const open = useRef<{ id: string; session: InteractionSession } | null>(null);

  const end = () => {
    open.current?.session.close();
    open.current = null;
  };

  // A field unmounting mid-edit (selection changed) still seals its entry.
  useEffect(() => end, []);

  return {
    id: () => {
      if (!open.current?.session.active) {
        const id = `${name}:${++sessionCount}`;
        const session = bus.openSession(name, () => {
          if (open.current?.id === id) open.current = null;
        });
        open.current = { id, session };
      }
      return open.current.id;
    },
    end,
  };
}
